import '../../../assets/styles/PromptView.css'
import './styles.css'

import React, { useEffect, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { useUpdateArtifacts } from '../../../hooks/apiHooks/artifactsHooks'
import {
  useMentorPsychometricsById,
  usePersonaById,
  usePrompts,
  useUpdatePersona,
  useUpdatePsychometrics,
} from '../../../hooks/apiHooks/personasHooks'
import { useSession } from '../../../hooks/storeHooks/useSessionState'
import { ArtifactItem, SessionMentor } from '../../../types/mentor'
import {
  MentorPsychometrics,
  PsychometricsCategory,
  PsychometricsSubcategories,
} from '../../../types/persona'
import { ArtifactsItem } from './components/ArtifactsItem'
import { Header } from './components/Header'
import { ProfileTab } from './components/ProfileTab'
import { PromptInput } from './components/PromptInput'
import { PsychometricAnalysis } from './components/PsychometricAnalysis'

interface PromptViewProps {
  mentorId?: string
  personaData: SessionMentor | null
}

export type TouchcastPsychomenticsTypes =
  | MentorPsychometrics['touchcastPsychometrics']['socialDynamics']
  | MentorPsychometrics['touchcastPsychometrics']['workStyle']
  | MentorPsychometrics['touchcastPsychometrics']['core']
  | undefined

export const PromptView: React.FC<PromptViewProps> = ({
  mentorId,
  personaData,
}) => {
  const [searchParams] = useSearchParams()
  const startParam = searchParams.get('start')

  const { isAuthenticated } = useAuth()
  const { promptData, setPromptData } = useSession()

  const [updateNeeded, setIsUpdateNeeded] = useState(false)
  const [artifactUpdateNeeded, setIsArtifactUpdateNeeded] = useState(false)
  const [showPrompt, setShowPrompt] = useState(false)
  const [artifactsData, setArtifacts] = useState<ArtifactItem[]>([])
  const [activeTab, setActiveTab] = React.useState<
    'profile' | 'psychometric' | 'artifacts' | 'prompt'
  >('profile')

  const [psychometricUpdateNeeded, setIsPsychometricUpdateNeeded] =
    useState(false)

  const { data: promptsData } = usePrompts(
    mentorId || '',
    isAuthenticated && showPrompt,
  )

  const { data: profileData, isLoading: isProfileLoading } = usePersonaById(
    mentorId ?? '',
    isAuthenticated && showPrompt,
  )
  const { data: mentorPsychometrics, isLoading: isMentorPsychometricsLoading } =
    useMentorPsychometricsById(mentorId ?? '', isAuthenticated && showPrompt)
  const { mutateAsync: updateArtifacts, isPending: isUpdateArtifactsPending } =
    useUpdateArtifacts()
  const { mutateAsync: updatePersona, isPending: isUpdatePersonaPending } =
    useUpdatePersona()
  const { mutateAsync: updatePsychometrics, isPending: isUpdatePMPending } =
    useUpdatePsychometrics()
  const [updatedPM, setUpdatedPM] = useState<MentorPsychometrics | null>(null)

  useEffect(() => {
    if (!showPrompt) {
      setIsPsychometricUpdateNeeded(false)
      setIsUpdateNeeded(false)
      setIsArtifactUpdateNeeded(false)
    }
  }, [showPrompt])

  useEffect(() => {
    if (mentorPsychometrics) {
      const updatedData = JSON.parse(
        JSON.stringify(mentorPsychometrics),
      ) as MentorPsychometrics
      setUpdatedPM(updatedData)
    }
  }, [mentorPsychometrics])

  const selectTab =
    (tabs: 'profile' | 'psychometric' | 'artifacts' | 'prompt') => () => {
      setIsPsychometricUpdateNeeded(false)
      setActiveTab(tabs)
    }

  const handlePromptChange: React.ChangeEventHandler<
    HTMLTextAreaElement
  > = event => {
    if (promptsData?.avatarAssistantPrompt !== event.target.value) {
      setIsUpdateNeeded(true)
    } else {
      setIsUpdateNeeded(false)
    }
    setPromptData(event.target.value)
  }

  const handleUpdateArtifacts = (updatedArtifact: ArtifactItem) => {
    setIsArtifactUpdateNeeded(true)
    setArtifacts(prevState =>
      prevState.map(artifact =>
        artifact.id === updatedArtifact.id ? updatedArtifact : artifact,
      ),
    )
  }

  const handlePromptClose = () => {
    setShowPrompt(false)
  }

  const handleKeyPress = (event: { ctrlKey: boolean; key: string }) => {
    if (event.ctrlKey && event.key === 'l') {
      setShowPrompt(true)
    }
  }

  const saveResponse = async () => {
    try {
      if (activeTab === 'artifacts' && artifactUpdateNeeded) {
        await updateArtifacts({
          artifacts: artifactsData,
          mentor_id: personaData?.id || '',
        })
        setIsArtifactUpdateNeeded(false)
      } else if (
        activeTab === 'psychometric' &&
        psychometricUpdateNeeded &&
        updatedPM
      ) {
        await updatePsychometrics({
          updatedPM,
          mentorId: personaData?.id || '',
        })
        setIsPsychometricUpdateNeeded(false)
      } else if (activeTab === 'prompt' && updateNeeded) {
        await updatePersona({
          mentorId: personaData?.id || '',
          assistantPrompt: promptData || '',
        })
        setIsUpdateNeeded(false)
      } else {
        return
      }
      if (startParam) {
        window.location.replace(
          `${location.pathname}${startParam ? `?start=${startParam}` : ''}`,
        )
      }
      toast.success('Updated successfully')
      setShowPrompt(false)
    } catch (error) {
      console.error(`Error updating ${activeTab}:`, error)
      toast.error('Failed to update')
    }
  }

  const handleSave = () => {
    saveResponse().catch(error => {
      console.error('Error saving response:', error)
      toast.error('Failed to save changes')
    })
  }

  useEffect(() => {
    if (promptsData?.avatarAssistantPrompt) {
      setPromptData(promptsData.avatarAssistantPrompt)
      setArtifacts(promptsData.artifactPrompts)
    }
  }, [promptsData])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  const handleBig5update = (data: MentorPsychometrics['big5']) => {
    setUpdatedPM((prevState: MentorPsychometrics | null) => {
      if (!prevState) {
        return null
      }
      return {
        ...prevState,
        big5: data,
      }
    })
  }

  const handleConversationDataUpdate = (data: { [key: string]: number }) => {
    setUpdatedPM((prevState: MentorPsychometrics | null) => {
      if (!prevState) {
        return null
      }
      const categories = JSON.parse(
        JSON.stringify(prevState?.conversationData?.categories),
      ) as PsychometricsCategory[]
      for (const key in data) {
        const [first, second] = key.split('_')
        const categoryIndex = categories.findIndex(
          (cat: PsychometricsCategory) => cat.categoryTitle === first,
        )
        if (categoryIndex !== -1) {
          const subCatIndex = categories[categoryIndex].subcategories.findIndex(
            (sub: PsychometricsSubcategories) => sub.name === second,
          )
          if (subCatIndex !== -1) {
            categories[categoryIndex].subcategories[subCatIndex].score =
              data[key]
          }
        }
      }
      return {
        ...prevState,
        conversationData: {
          ...prevState?.conversationData,
          categories,
        },
      }
    })
  }
  const handleTouchcastPsychometricsUpdate = (
    key: string,
    data: TouchcastPsychomenticsTypes,
  ) => {
    setUpdatedPM((prevState: MentorPsychometrics | null) => {
      if (!prevState) {
        return null
      }
      return {
        ...prevState,
        touchcastPsychometrics: {
          ...prevState?.touchcastPsychometrics,
          [key]: data,
        },
      }
    })
  }

  const disabledSubmitButton =
    (!artifactUpdateNeeded && activeTab === 'artifacts') ||
    (!updateNeeded && activeTab === 'prompt') ||
    (!psychometricUpdateNeeded && activeTab === 'psychometric') ||
    activeTab === 'profile' ||
    isUpdatePersonaPending ||
    isUpdateArtifactsPending ||
    isUpdatePMPending
  if (!showPrompt) return null

  return (
    <div className="start-exp-lightbox-wrapper edit-prompt !bg-transparent">
      <div className="ai-pg-form-wrapper">
        <div className="ai-pg-form-container tc-mv-blur relative">
          <button
            title="Close"
            className="p-2 hover:bg-white/10 rounded-full absolute top-4 right-3 z-20"
            onClick={handlePromptClose}
          >
            <i className="fa-solid fa-xmark text-xl text-white" />
          </button>
          <div className="w-full h-[calc(100%-56px)] z-10">
            <div className="m-chat-prompt-field h-full flex flex-col">
              <Header
                {...profileData?.data.profile.profile}
                isLoading={isProfileLoading}
              />
              <div className="h-11 w-max mb-4 rounded-[100px] flex gap-2 p-1 tab-btn-container tc-mv-question-field">
                <button
                  className={`tab-item${
                    activeTab === 'profile' ? ' active' : ''
                  }`}
                  onClick={selectTab('profile')}
                >
                  Profile
                </button>
                <button
                  className={`tab-item${
                    activeTab === 'psychometric' ? ' active' : ''
                  }`}
                  onClick={selectTab('psychometric')}
                >
                  Psychometric
                </button>
                <button
                  className={`tab-item${
                    activeTab === 'artifacts' ? ' active' : ''
                  }`}
                  onClick={selectTab('artifacts')}
                >
                  Artifacts
                </button>
                <button
                  className={`tab-item${
                    activeTab === 'prompt' ? ' active' : ''
                  }`}
                  onClick={selectTab('prompt')}
                >
                  Prompt
                </button>
              </div>
              <div className="flex-1 w-full overflow-y-auto space-y-4 [&::-webkit-scrollbar]:hidden">
                {activeTab === 'profile' && (
                  <ProfileTab profile={profileData?.data.profile.profile} />
                )}
                {activeTab === 'psychometric' && (
                  <PsychometricAnalysis
                    mentorPsychometrics={mentorPsychometrics}
                    isLoading={isMentorPsychometricsLoading}
                    onBig5Update={handleBig5update}
                    onConversationDataUpdate={handleConversationDataUpdate}
                    onTouchcastPsychometricsUpdate={
                      handleTouchcastPsychometricsUpdate
                    }
                    setIsPsychometricUpdateNeeded={
                      setIsPsychometricUpdateNeeded
                    }
                  />
                )}
                {activeTab === 'artifacts' && (
                  <div className="h-full space-y-4 text-white">
                    {artifactsData?.map(artifact => (
                      <ArtifactsItem
                        artifact={artifact}
                        handleChange={handleUpdateArtifacts}
                        key={artifact.id}
                      />
                    ))}
                  </div>
                )}
                {activeTab === 'prompt' && (
                  <PromptInput
                    promptData={promptData || ''}
                    onPromptChange={handlePromptChange}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="ai-form-wrapper z-20">
            <div className="btn-restart-section-block" />
            <div className="ai-speaker-generate-btn-block">
              <button
                className="btn btn-secondary btn-small"
                onClick={handlePromptClose}
              >
                Close
              </button>
              <button
                className="btn bg-green-100 hover:bg-green-200 transition-bg btn-small disabled:opacity-70"
                onClick={handleSave}
                disabled={disabledSubmitButton}
              >
                {(isUpdatePersonaPending ||
                  isUpdateArtifactsPending ||
                  isUpdatePMPending) && (
                  <span className="me-2">
                    <AnimatedSpinner />
                  </span>
                )}
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
